import { ChevronDown, ChevronLeft, MessageCircle, Paperclip, Radio, Send, Users, X } from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { useAthletes } from '../../contexts/AthletesContext'
import { useChatContext } from '../../contexts/ChatContext'
import { useRole } from '../../contexts/RoleContext'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import type { ChatConversation, ChatMessage as ChatMessageInterface, ChatType } from '../../types/chat'
import type { Database } from '../../types/database.types'
import { getAvatarUrl } from '../../utils/avatarUtils'
import { ChatConversationItem } from './ChatConversationItem'
import { ChatMessage } from './ChatMessage'

type ChatWithProfiles = Database['public']['Tables']['chats']['Row'] & {
  athlete_profile?: {
    first_name: string | null
    last_name: string | null
    avatar_url: string | null
  }
  coach_profile?: {
    first_name: string | null
    last_name: string | null
    avatar_url: string | null
  }
}

const CHAT_MAX_WIDTH = 1600

const ChatContainer = styled.div<{ isResizing?: boolean }>`
  position: fixed;
  bottom: ${theme.spacing.md};
  right: ${theme.spacing.md};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  cursor: ${(props) => (props.isResizing ? 'nwse-resize' : 'default')};
  user-select: ${(props) => (props.isResizing ? 'none' : 'auto')};
  max-width: 100vw;
`

const ChatButton = styled.button<{ isOpen: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${(props) => (props.isOpen ? 'rotate(0deg)' : 'rotate(0deg)')};
  position: relative;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: ${(props) => (props.isOpen ? 'scale(1.05) rotate(0deg)' : 'scale(1.1) rotate(0deg)')};
  }

  &:active {
    transform: scale(0.95);
  }
`

const UnreadBadge = styled.div<{ count: number }>`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  background-color: #ef4444;
  color: white;
  font-size: ${theme.typography.xs};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  animation: badgeEntrance 0.3s cubic-bezier(0.4, 0, 0.2, 1),
             ${(props) => (props.count > 0 ? 'pulse 2s ease-in-out infinite 0.5s' : 'none')};
  border: 2px solid ${({ theme }) => theme.colors.background};

  @keyframes badgeEntrance {
    0% {
      opacity: 0;
      transform: scale(0);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`

const ChatPopup = styled.div<{
  isOpen: boolean
  width: number
  height: number
  isResizing: boolean
  hasBeenOpened: boolean
}>`
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  flex-direction: column;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  min-width: 320px;
  min-height: 400px;
  max-width: 90%;
  max-height: 90vh;
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  margin-bottom: ${theme.spacing.md};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  transform-origin: bottom right;
  position: relative;

  /* Opening animation */
  ${(props) =>
    props.isOpen &&
    `
    animation: chatPopupOpen 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  `}

  /* Closing animation - only if chat has been opened before (prevents flash on initial load) */
  ${(props) =>
    !props.isOpen &&
    props.hasBeenOpened &&
    `display: flex;
    animation: chatPopupClose 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    pointer-events: none;
    `}

  @keyframes chatPopupOpen {
    0% {
      opacity: 0;
      transform: scale(0.9) translateY(10px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @keyframes chatPopupClose {
    0% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
    99% {
      opacity: 0;
      transform: scale(0.9) translateY(10px);
      display: flex;
    }
    100% {
      opacity: 0;
      transform: scale(0.9) translateY(10px);
      display: none;
    }
  }
`

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  cursor: nwse-resize;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 8px;
    left: 8px;
    width: 8px;
    height: 8px;
    border-top: 2px solid ${({ theme }) => theme.colors.border};
    border-left: 2px solid ${({ theme }) => theme.colors.border};
    opacity: 0.6;
  }

  &:hover::after {
    opacity: 1;
    border-color: ${({ theme }) => theme.colors.text};
  }
`

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const ChatHeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`

const ChatHeaderAvatar = styled.div<{ $chatId?: string; $isActive?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.typography.sm};
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  overflow: hidden;
  flex-shrink: 0;
  contain: layout;
  view-transition-name: ${({ $chatId, $isActive }) => ($chatId && $isActive ? `chat-avatar-${$chatId}` : 'none')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ChatTitle = styled.h3<{ $chatId?: string; $isActive?: boolean }>`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  contain: layout;
  view-transition-name: ${({ $chatId, $isActive }) => ($chatId && $isActive ? `chat-name-${$chatId}` : 'none')};
`

const HeaderActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xs};
  border-radius: 50%;
  transition: ${theme.transitions.default};
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`

const ChatContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
`

const ConversationList = styled.div<{ isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${theme.spacing.sm} 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${(props) => (props.isVisible ? '1' : '0')};
  transform: translateX(${(props) => (props.isVisible ? '0' : '-20px')});
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};

  & > * {
    animation: ${(props) => (props.isVisible ? 'slideInFromLeft 0.3s cubic-bezier(0.4, 0, 0.2, 1) backwards' : 'none')};
  }

  & > *:nth-child(1) { animation-delay: 0.05s; }
  & > *:nth-child(2) { animation-delay: 0.1s; }
  & > *:nth-child(3) { animation-delay: 0.15s; }
  & > *:nth-child(4) { animation-delay: 0.2s; }
  & > *:nth-child(5) { animation-delay: 0.25s; }

  @keyframes slideInFromLeft {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`

const ConversationView = styled.div<{ isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${(props) => (props.isVisible ? '1' : '0')};
  transform: translateX(${(props) => (props.isVisible ? '0' : '20px')});
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
`

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;

  & > div[class*="MessageContainer"] {
    animation: fadeInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const InputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  padding: ${theme.spacing.sm};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  flex: 0 0 auto;
`

const MessageInput = styled.textarea`
  flex: 1;
  border: none;
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  outline: none;
  resize: none;
  min-height: 36px;
  max-height: 120px;
  overflow-y: auto;
  font-family: inherit;
  font-size: ${theme.typography.md};
  line-height: 1.5;

  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryDark};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`

const SendButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: ${theme.spacing.sm};
  margin-bottom: 2px;
  cursor: pointer;
  transition: ${theme.transitions.default};
  flex-shrink: 0;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
`

const AttachButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xs};
  transition: ${theme.transitions.default};

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sm};
  margin-bottom: ${theme.spacing.xs};
`

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 2px;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: ${theme.spacing.xs};
  margin-right: ${theme.spacing.xs};

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`

const ChatTypeIndicator = styled.span<{ type: ChatType }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${theme.typography.xs};
  color: ${({ theme, type }) =>
    type === 'broadcast' ? '#ff6b35' : type === 'group' ? '#4ecdc4' : theme.colors.textSecondary};
  margin-top: 2px;
`

const GroupTag = styled.span`
  background-color: rgba(78, 205, 196, 0.1);
  color: #4ecdc4;
  padding: 2px 6px;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.xs};
  font-weight: 500;
  margin-left: ${theme.spacing.xs};
`

export const Chat: React.FC = () => {
  const { currentRole } = useRole()
  const location = useLocation()
  const { athletes } = useAthletes()

  // Fetch chats and messages using ChatContext
  const {
    chats,
    chatsLoading,
    createChatsForAthletes,
    messages,
    messagesLoading,
    loadingMore,
    hasMore,
    sendMessage,
    markAsRead,
    loadMoreMessages,
    activeChatId,
    setActiveChatId,
    isChatOpen,
    setIsChatOpen,
  } = useChatContext()

  // Extract athlete ID from URL path (e.g., /athletes/:id)
  const athleteIdFromRoute = useMemo(() => {
    const pathMatch = location.pathname.match(/\/athletes\/([^/]+)/)
    return pathMatch ? pathMatch[1] : null
  }, [location.pathname])

  // Get the active chat object from the chats array
  const activeChat = useMemo(() => {
    return chats.find((chat) => chat.id === activeChatId) || null
  }, [chats, activeChatId])

  const [hasBeenOpened, setHasBeenOpened] = useState(false)
  const [activeConversation, setActiveConversation] = useState<ChatConversation | null>(null)
  const [manuallyNavigatedAway, setManuallyNavigatedAway] = useState(false)
  const [newMessage, setNewMessage] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [mockMessages, setMockMessages] = useState<ChatMessageInterface[]>([])
  const [chatWidth, setChatWidth] = useState(() => {
    try {
      const stored = localStorage.getItem('chatWidth')
      return stored ? Number.parseInt(stored, 10) : 380
    } catch {
      return 380
    }
  })
  const [chatHeight, setChatHeight] = useState(() => {
    try {
      const stored = localStorage.getItem('chatHeight')
      return stored ? Number.parseInt(stored, 10) : 600
    } catch {
      return 600
    }
  })
  const [isResizing, setIsResizing] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const messagesStartRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const resizeStartPos = useRef({ x: 0, y: 0, width: 0, height: 0 })
  const previousMessageCountRef = useRef<number>(0)
  const lastMessageIdRef = useRef<string | null>(null)
  const isInitialLoadRef = useRef<boolean>(true)

  // Store draft messages per conversation - initialize from localStorage
  const [draftMessages, setDraftMessages] = useState<Record<string, string>>(() => {
    try {
      const stored = localStorage.getItem('chatDrafts')
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })

  // Persist draft messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('chatDrafts', JSON.stringify(draftMessages))
    } catch (error) {
      console.error('Failed to save draft messages to localStorage:', error)
    }
  }, [draftMessages])

  // Persist chat open state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('chatIsOpen', JSON.stringify(isChatOpen))
    } catch (error) {
      console.error('Failed to save chat open state to localStorage:', error)
    }
  }, [isChatOpen])

  // Persist chat window dimensions to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('chatWidth', chatWidth.toString())
      localStorage.setItem('chatHeight', chatHeight.toString())
    } catch (error) {
      console.error('Failed to save chat dimensions to localStorage:', error)
    }
  }, [chatWidth, chatHeight])

  // Get current user
  useEffect(() => {
    const getCurrentUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      setCurrentUserId(user?.id || null)
    }
    getCurrentUser()
  }, [])

  // Mock data for broadcast and group conversations - useMemo to avoid recreating on every render
  const mockBroadcastConversations: ChatConversation[] = useMemo(
    () => [
      {
        id: 'broadcast_1',
        type: 'broadcast',
        name: 'Broadcast',
        participants: [currentUserId || 'coach1'],
        isAdmin: true,
        lastMessage: {
          id: 'msg_b1',
          senderId: currentUserId || 'coach1',
          content: 'New training plan available for next week',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          read: true,
        },
        unreadCount: 0,
      },
    ],
    [currentUserId]
  )

  const mockGroupConversations: ChatConversation[] = useMemo(
    () => [
      {
        id: 'group_1',
        type: 'group',
        name: 'Elite Training Squad',
        participants: [currentUserId || 'coach1', 'athlete1', 'athlete2', 'athlete3'],
        lastMessage: {
          id: 'msg_g1',
          senderId: 'athlete2',
          content: 'Great session today! See you all tomorrow',
          timestamp: new Date(Date.now() - 1800000).toISOString(),
          read: false,
        },
        unreadCount: 2,
      },
      {
        id: 'group_2',
        type: 'group',
        name: 'Weekend Warriors',
        participants: [currentUserId || 'coach1', 'athlete4', 'athlete5', 'athlete6', 'athlete7'],
        lastMessage: {
          id: 'msg_g2',
          senderId: 'athlete5',
          content: 'What time is the group run on Saturday?',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          read: true,
        },
        unreadCount: 0,
      },
    ],
    [currentUserId]
  )

  // Automatically switch to athlete's chat when coach navigates to an athlete page
  useEffect(() => {
    if (currentRole !== 'coach' || !athleteIdFromRoute || !currentUserId || chatsLoading) {
      return
    }

    // Don't auto-switch if user has manually navigated away
    if (manuallyNavigatedAway) {
      return
    }

    // Find the chat with this athlete
    const athleteChat = chats.find((chat) => chat.athlete_id === athleteIdFromRoute)
    if (!athleteChat) {
      console.log('No chat found for athlete:', athleteIdFromRoute)
      return
    }
    console.log('Automatically switching to chat for athlete:', athleteChat.athlete_id)

    // Switch to this chat if it's not already active
    if (activeChatId !== athleteChat.id) {
      setActiveChatId(athleteChat.id)
      setActiveConversation(null)
    }
  }, [
    currentRole,
    athleteIdFromRoute,
    chats,
    chatsLoading,
    activeChatId,
    currentUserId,
    manuallyNavigatedAway,
    setActiveChatId,
  ])

  // Reset manual navigation flag when route changes to a different athlete
  // biome-ignore lint/correctness/useExhaustiveDependencies: extra trigger required for this case
  useEffect(() => {
    setManuallyNavigatedAway(false)
  }, [athleteIdFromRoute])

  // Mark messages as read when viewing a chat
  useEffect(() => {
    if (!activeChatId || !currentUserId || messages.length === 0) {
      return
    }

    // Find unread messages that were sent by someone else
    const unreadMessageIds = messages
      .filter((msg) => msg.sender_id !== currentUserId && msg.status !== 'read')
      .map((msg) => msg.id)

    if (unreadMessageIds.length > 0) {
      markAsRead(unreadMessageIds).catch((err) => {
        console.error('Failed to mark messages as read:', err)
      })
    }
  }, [activeChatId, currentUserId, messages, markAsRead])

  const generateMockMessages = useCallback(
    (conversation: ChatConversation): ChatMessageInterface[] => {
      const mockMsgs: ChatMessageInterface[] = []

      if (conversation.type === 'broadcast') {
        mockMsgs.push(
          {
            id: 'bmsg1',
            senderId: currentUserId || 'coach1',
            content: `Welcome to the ${conversation.name} channel!`,
            timestamp: new Date(Date.now() - 86400000).toISOString(),
            read: true,
          },
          {
            id: 'bmsg2',
            senderId: currentUserId || 'coach1',
            content: conversation.lastMessage.content,
            timestamp: conversation.lastMessage.timestamp,
            read: true,
          }
        )
      } else if (conversation.type === 'group') {
        mockMsgs.push(
          {
            id: 'gmsg1',
            senderId: 'athlete1',
            content: "Hey everyone! Ready for tomorrow's training?",
            timestamp: new Date(Date.now() - 7200000).toISOString(),
            read: true,
          },
          {
            id: 'gmsg2',
            senderId: currentUserId || 'coach1',
            content: 'Looking forward to it! Remember to bring your heart rate monitors.',
            timestamp: new Date(Date.now() - 5400000).toISOString(),
            read: true,
          },
          {
            id: 'gmsg3',
            senderId: conversation.lastMessage.senderId,
            content: conversation.lastMessage.content,
            timestamp: conversation.lastMessage.timestamp,
            read: false,
          }
        )
      }

      return mockMsgs
    },
    [currentUserId]
  )

  // Generate mock messages when active conversation changes
  useEffect(() => {
    if (activeConversation) {
      const generatedMessages = generateMockMessages(activeConversation)
      setMockMessages(generatedMessages)
    }
  }, [activeConversation, generateMockMessages])

  // Load draft message when switching conversations
  const activeConversationId = activeChat?.id || activeConversation?.id
  useEffect(() => {
    if (activeConversationId) {
      const draft = draftMessages[activeConversationId] || ''
      setNewMessage(draft)
    } else {
      setNewMessage('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId, draftMessages]) // Only trigger when conversation ID changes, not when draftMessages changes

  // Save draft message when input changes
  const handleMessageChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value
      setNewMessage(value)

      // Auto-resize textarea
      const textarea = e.target
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`

      const conversationId = activeChat?.id || activeConversation?.id
      if (conversationId) {
        setDraftMessages((prev) => ({
          ...prev,
          [conversationId]: value,
        }))
      }
    },
    [activeChat, activeConversation]
  )

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Reset textarea height when message is cleared or conversation changes
  useEffect(() => {
    if (textareaRef.current && newMessage === '') {
      textareaRef.current.style.height = 'auto'
    }
  }, [newMessage])

  // Reset textarea height when conversation changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: extra trigger required for this case
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [activeConversationId])

  // Resize handlers
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setIsResizing(true)
      resizeStartPos.current = {
        x: e.clientX,
        y: e.clientY,
        width: chatWidth,
        height: chatHeight,
      }
    },
    [chatWidth, chatHeight]
  )

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartPos.current.x - e.clientX
      const deltaY = resizeStartPos.current.y - e.clientY

      const newWidth = Math.max(320, Math.min(CHAT_MAX_WIDTH, resizeStartPos.current.width + deltaX))
      const newHeight = Math.max(400, Math.min(window.innerHeight * 0.9, resizeStartPos.current.height + deltaY))

      setChatWidth(newWidth)
      setChatHeight(newHeight)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  // Intersection Observer for infinite scroll
  // biome-ignore lint/correctness/useExhaustiveDependencies: extra trigger messages.length
  useEffect(() => {
    if (!messagesStartRef.current || !hasMore || loadingMore || !activeChat) return

    // Add a small delay to prevent triggering on initial mount
    const timeoutId = setTimeout(() => {
      if (!messagesStartRef.current) return

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !loadingMore) {
            loadMoreMessages()
          }
        },
        {
          threshold: 0.1,
          root: messagesContainerRef.current,
          rootMargin: '20px',
        }
      )

      observer.observe(messagesStartRef.current)

      return () => {
        observer.disconnect()
      }
    }, 300)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [hasMore, loadingMore, loadMoreMessages, activeChat, messages.length])

  // Only scroll to bottom when new messages are added (not when loading older messages)
  useEffect(() => {
    const currentMessages = activeChat ? messages : mockMessages
    const currentLastMessageId = currentMessages.length > 0 ? currentMessages[currentMessages.length - 1].id : null

    // On initial load, scroll immediately without animation
    if (isInitialLoadRef.current && currentMessages.length > 0) {
      isInitialLoadRef.current = false
      previousMessageCountRef.current = currentMessages.length
      lastMessageIdRef.current = currentLastMessageId
      // Instant scroll without smooth behavior
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
      return
    }

    // Scroll only if last message ID changed (new message at the end)
    if (lastMessageIdRef.current !== currentLastMessageId && currentLastMessageId !== null) {
      scrollToBottom()
    }

    previousMessageCountRef.current = currentMessages.length
    lastMessageIdRef.current = currentLastMessageId
  }, [messages, mockMessages, activeChat, scrollToBottom])

  // Reset initial load flag when conversation changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: extra trigger required for this case
  useEffect(() => {
    isInitialLoadRef.current = true
  }, [activeChat?.id, activeConversation?.id])

  const createChats = useCallback(async () => {
    if (currentRole !== 'coach' || !currentUserId) {
      return
    }

    // Ensure that a chat exists for every athlete
    const athleteIds = athletes.map((athlete) => athlete.id)
    await createChatsForAthletes(athleteIds, currentUserId)
  }, [currentRole, currentUserId, athletes, createChatsForAthletes])

  const toggleChat = () => {
    if (!isChatOpen) {
      setHasBeenOpened(true)
      createChats()
    }
    setIsChatOpen(!isChatOpen)
  }

  const closeChat = () => {
    setIsChatOpen(false)
  }

  const openChat = (chat: ChatWithProfiles) => {
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        flushSync(() => {
          setActiveChatId(chat.id)
          setActiveConversation(null)
        })
      })
    } else {
      setActiveChatId(chat.id)
      setActiveConversation(null)
    }
  }

  const openConversation = (conversation: ChatConversation) => {
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        flushSync(() => {
          setActiveConversation(conversation)
          setActiveChatId(null)
        })
      })
    } else {
      setActiveConversation(conversation)
      setActiveChatId(null)
    }
  }

  const backToChats = () => {
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        flushSync(() => {
          setActiveChatId(null)
          setActiveConversation(null)
          // Mark that user has manually navigated away from the auto-selected chat
          setManuallyNavigatedAway(true)
        })
      })
    } else {
      setActiveChatId(null)
      setActiveConversation(null)
      // Mark that user has manually navigated away from the auto-selected chat
      setManuallyNavigatedAway(true)
    }
  }

  const handleSendMessage = async () => {
    if ((newMessage.trim() === '' && !selectedFile) || !currentUserId) return

    const conversationId = activeChat?.id || activeConversation?.id

    // Handle real database messages
    if (activeChat) {
      try {
        await sendMessage(newMessage, currentUserId, selectedFile || undefined)
        setNewMessage('')
        setSelectedFile(null)

        // Clear draft for this conversation
        if (conversationId) {
          setDraftMessages((prev) => {
            const updated = { ...prev }
            delete updated[conversationId]
            return updated
          })
        }
      } catch (error) {
        console.error('Failed to send message:', error)
        alert('Failed to send message. Please try again.')
      }
    }

    // Handle mock messages for broadcast/group
    if (activeConversation) {
      const newMsg: ChatMessageInterface = {
        id: `msg${Date.now()}`,
        senderId: currentUserId,
        content: newMessage,
        timestamp: new Date().toISOString(),
        read: false,
      }
      setMockMessages([...mockMessages, newMsg])
      setNewMessage('')

      // Clear draft for this conversation
      if (conversationId) {
        setDraftMessages((prev) => {
          const updated = { ...prev }
          delete updated[conversationId]
          return updated
        })
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send message on Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleFileSelect = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Check file size (20MB limit)
      if (file.size > 20 * 1024 * 1024) {
        alert('File size must be less than 20MB')
        return
      }
      setSelectedFile(file)
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
  }

  const getChatDisplayName = (chat: ChatWithProfiles): string => {
    if (!currentUserId) return 'Chat'

    // Show the other person's name
    if (chat.athlete_id === currentUserId) {
      // Current user is athlete, show coach name
      const coach = chat.coach_profile
      if (coach?.first_name && coach?.last_name) {
        return `${coach.first_name} ${coach.last_name}`
      }
      return 'Coach'
    } else {
      // Current user is coach, show athlete name
      const athlete = chat.athlete_profile
      if (athlete?.first_name && athlete?.last_name) {
        return `${athlete.first_name} ${athlete.last_name}`
      }
      return 'Athlete'
    }
  }

  const getChatAvatar = (chat: ChatWithProfiles): string | null => {
    if (!currentUserId) return null

    // Get the other person's avatar
    if (chat.athlete_id === currentUserId) {
      return chat.coach_profile?.avatar_url || null
    } else {
      return chat.athlete_profile?.avatar_url || null
    }
  }

  const getChatInitials = (chat: ChatWithProfiles): string => {
    const name = getChatDisplayName(chat)
    return name.charAt(0).toUpperCase()
  }

  // Helper function to determine if timestamp should be shown
  // Show timestamp if it's more than 5 minutes apart from previous message
  const shouldShowTimestamp = (currentTimestamp: string, previousTimestamp?: string): boolean => {
    if (!previousTimestamp) return true

    const current = new Date(currentTimestamp).getTime()
    const previous = new Date(previousTimestamp).getTime()
    const diffMinutes = (current - previous) / (1000 * 60)

    return diffMinutes >= 5
  }

  const getAllConversations = (): ChatConversation[] => {
    // Convert real chats to conversation format
    const directConversations: ChatConversation[] = chats.map((chat) => ({
      id: chat.id,
      type: 'direct' as const,
      name: getChatDisplayName(chat),
      participants: [chat.athlete_id, chat.coach_id],
      lastMessage: {
        id: chat.id,
        senderId: chat.last_message_sender_id || '',
        content: chat.last_message || 'No messages yet',
        timestamp: chat.last_message_time || chat.created_at,
        read: chat.last_message_sender_id === currentUserId,
      },
      unreadCount: chat.unread_count || 0,
    }))

    // Combine all conversation types and sort by most recent
    const allConversations = [...mockBroadcastConversations, ...mockGroupConversations, ...directConversations]

    return allConversations.sort((a, b) => {
      const timestampA = new Date(a.lastMessage.timestamp).getTime()
      const timestampB = new Date(b.lastMessage.timestamp).getTime()
      return timestampB - timestampA // Most recent first
    })
  }

  const getConversationDisplayName = (conversation: ChatConversation | null): string => {
    return conversation?.name || 'Unknown Chat'
  }

  // Calculate total unread messages
  const getTotalUnreadCount = useCallback((): number => {
    if (!currentUserId) return 0

    // Count unread messages from database chats
    const dbUnreadCount = chats.reduce((count, chat) => {
      return count + (chat.unread_count ?? 0)
    }, 0)

    // Count unread from mock conversations (broadcast/group)
    const mockUnreadCount = 0
    /*
    const mockUnreadCount = [...mockBroadcastConversations, ...mockGroupConversations].reduce((count, conv) => {
      return count + conv.unreadCount
    }, 0)
     */

    return dbUnreadCount + mockUnreadCount
  }, [chats, currentUserId])

  if (!currentUserId) {
    return null // Don't render chat if user is not authenticated
  }

  const activeItem = activeChat || activeConversation
  const unreadCount = getTotalUnreadCount()

  return (
    <ChatContainer isResizing={isResizing}>
      <ChatPopup
        isOpen={isChatOpen}
        width={chatWidth}
        height={chatHeight}
        isResizing={isResizing}
        hasBeenOpened={hasBeenOpened}
      >
        <ResizeHandle onMouseDown={handleResizeStart} />
        <ChatHeader>
          {activeItem ? (
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <BackButton onClick={backToChats}>
                <ChevronLeft size={18} />
              </BackButton>
              {activeChat && (
                <ChatHeaderAvatar $chatId={activeChat.id} $isActive={true}>
                  {getChatAvatar(activeChat) ? (
                    <img src={getAvatarUrl(getChatAvatar(activeChat))} alt={getChatDisplayName(activeChat)} />
                  ) : (
                    getChatInitials(activeChat)
                  )}
                </ChatHeaderAvatar>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <ChatTitle $chatId={activeChat?.id} $isActive={!!activeChat}>
                  {activeChat ? getChatDisplayName(activeChat) : getConversationDisplayName(activeConversation)}
                </ChatTitle>
                {activeConversation && (
                  <ChatTypeIndicator type={activeConversation.type}>
                    {activeConversation.type === 'broadcast' && (
                      <>
                        <Radio size={12} /> Broadcast
                      </>
                    )}
                    {activeConversation.type === 'group' && (
                      <>
                        <GroupTag>GROUP</GroupTag>
                        <Users size={12} /> {activeConversation.participants.length} members
                      </>
                    )}
                    {activeConversation.type === 'direct' && <>Direct message</>}
                  </ChatTypeIndicator>
                )}
              </div>
            </div>
          ) : (
            <ChatTitle>Messages</ChatTitle>
          )}
          <HeaderActions>
            <IconButton onClick={closeChat}>
              <X size={18} />
            </IconButton>
          </HeaderActions>
        </ChatHeader>

        <ChatContent>
          <ConversationList isVisible={!activeItem}>
            {chatsLoading ? (
              <div style={{ padding: theme.spacing.md, textAlign: 'center', color: theme.colors.textSecondary }}>
                Loading chats...
              </div>
            ) : getAllConversations().length === 0 ? (
              <div style={{ padding: theme.spacing.md, textAlign: 'center', color: theme.colors.textSecondary }}>
                No chats yet
              </div>
            ) : (
              getAllConversations().map((conversation) => {
                // For direct chats, get the other user info
                let otherUser = null
                if (conversation.type === 'direct') {
                  // Find the corresponding chat from the database
                  const dbChat = chats.find((c) => c.id === conversation.id)
                  if (dbChat) {
                    const isUserAthlete = dbChat.athlete_id === currentUserId
                    const otherProfile = isUserAthlete ? dbChat.coach_profile : dbChat.athlete_profile
                    otherUser = {
                      id: isUserAthlete ? dbChat.coach_id : dbChat.athlete_id,
                      name: getChatDisplayName(dbChat),
                      avatar: otherProfile?.avatar_url || undefined,
                      online: false,
                    }
                  }
                }

                const handleClick = () => {
                  if (conversation.type === 'direct') {
                    const dbChat = chats.find((c) => c.id === conversation.id)
                    if (dbChat) {
                      openChat(dbChat)
                    }
                  } else {
                    openConversation(conversation)
                  }
                }

                const isActive =
                  (activeChat && activeChat.id === conversation.id) ||
                  (activeConversation && activeConversation.id === conversation.id)

                return (
                  <ChatConversationItem
                    key={conversation.id}
                    conversation={conversation}
                    otherUser={otherUser}
                    isActive={isActive ?? false}
                    onClick={handleClick}
                  />
                )
              })
            )}
          </ConversationList>

          <ConversationView isVisible={!!activeItem}>
            <MessagesContainer ref={messagesContainerRef}>
              {activeChat && loadingMore && (
                <div
                  ref={messagesStartRef}
                  style={{ textAlign: 'center', color: theme.colors.textSecondary, padding: theme.spacing.sm }}
                >
                  Loading more messages...
                </div>
              )}
              {activeChat && !loadingMore && hasMore && <div ref={messagesStartRef} style={{ height: '1px' }} />}
              {activeChat && messagesLoading ? (
                <div style={{ textAlign: 'center', color: theme.colors.textSecondary }}>Loading messages...</div>
              ) : activeChat && messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: theme.colors.textSecondary }}>
                  No messages yet. Start the conversation!
                </div>
              ) : activeChat ? (
                messages.map((message, index) => {
                  // Convert database message to chat message format
                  const chatMessage = {
                    id: message.id,
                    senderId: message.sender_id,
                    content: message.content,
                    timestamp: message.created_at,
                    read: message.status === 'read',
                    attachment_url: message.attachment_url,
                    attachment_name: message.attachment_name,
                    attachment_type: message.attachment_type,
                    attachment_size: message.attachment_size,
                  }
                  const previousMessage = index > 0 ? messages[index - 1] : null
                  const isLast = index === messages.length - 1
                  const showTimestamp = shouldShowTimestamp(message.created_at, previousMessage?.created_at) || isLast

                  return (
                    <ChatMessage
                      key={message.id}
                      message={chatMessage}
                      isOwnMessage={message.sender_id === currentUserId}
                      showTimestamp={showTimestamp}
                    />
                  )
                })
              ) : activeConversation && mockMessages.length === 0 ? (
                <div style={{ textAlign: 'center', color: theme.colors.textSecondary }}>
                  No messages yet. Start the conversation!
                </div>
              ) : (
                mockMessages.map((message, index) => {
                  const previousMessage = index > 0 ? mockMessages[index - 1] : null
                  const showTimestamp = shouldShowTimestamp(message.timestamp, previousMessage?.timestamp)

                  return (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      isOwnMessage={message.senderId === currentUserId}
                      showTimestamp={showTimestamp}
                    />
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </MessagesContainer>

            <InputContainer>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {selectedFile && (
                  <FilePreview>
                    <Paperclip size={14} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedFile.name}
                    </span>
                    <RemoveFileButton onClick={handleRemoveFile}>
                      <X size={14} />
                    </RemoveFileButton>
                  </FilePreview>
                )}
                <div style={{ display: 'flex', gap: theme.spacing.xs }}>
                  <AttachButton onClick={handleFileSelect}>
                    <Paperclip size={20} />
                  </AttachButton>
                  <MessageInput
                    ref={textareaRef}
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={handleMessageChange}
                    onKeyDown={handleKeyDown}
                    rows={1}
                  />
                </div>
              </div>
              <SendButton onClick={handleSendMessage}>
                <Send size={18} />
              </SendButton>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              />
            </InputContainer>
          </ConversationView>
        </ChatContent>
      </ChatPopup>

      <ChatButton onClick={toggleChat} isOpen={isChatOpen}>
        {!isChatOpen && <MessageCircle size={24} />}
        {isChatOpen && <ChevronDown size={24} />}
        {unreadCount > 0 && !isChatOpen && (
          <UnreadBadge count={unreadCount}>{unreadCount > 99 ? '99+' : unreadCount}</UnreadBadge>
        )}
      </ChatButton>
    </ChatContainer>
  )
}
